import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "../../../i18n";
import { athenaColors } from "../../../theme/palette";
import {
  athenaDarkSectionSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
} from "../../layout/AthenaDialogStyles";
import { createData } from "../../../api/apiHelpers";
import api from "../../../api/api";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "ca", label: "Català" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
];

const AGREEMENT_TO_CONSENT_MAP: Record<string, string> = {
  core: "core",
  module_b: "research",
  module_c: "third_party",
};

export type WizardStepConsentProps = {
  onComplete: () => void;
  onBack: () => void;
  agreementModules?: string[];
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function WizardStepConsent({ onComplete, onBack, agreementModules }: WizardStepConsentProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [language, setLanguage] = useState("en");
  const [generating, setGenerating] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const consentModules = useMemo(() => {
    if (!agreementModules?.length) return ["core", "research"];
    const mapped = agreementModules
      .map((m) => AGREEMENT_TO_CONSENT_MAP[m])
      .filter(Boolean);
    return mapped.length > 0 ? mapped : ["core", "research"];
  }, [agreementModules]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const resp = await api.post(
        "/billing/legal/consent/generate-pdf/",
        { language, modules: consentModules },
        { responseType: "blob" }
      );
      const blob = resp.data as Blob;
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
    } catch {
      setError(t("contracts_wizard.consent.generate_error", "Failed to generate consent template."));
    } finally {
      setGenerating(false);
    }
  }, [language, consentModules, t]);

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    fetch(blobUrl)
      .then((r) => r.blob())
      .then((blob) => downloadBlob(blob, `patient_consent_${language}.pdf`));
  }, [blobUrl, language]);

  const handleComplete = useCallback(async () => {
    if (!acknowledged) return;
    setAcknowledging(true);
    setError(null);
    try {
      await createData("/billing/legal/consent/acknowledge/", {});
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      onComplete();
    } catch {
      setError(t("contracts_wizard.consent.acknowledge_error", "Failed to record acknowledgment."));
    } finally {
      setAcknowledging(false);
    }
  }, [acknowledged, blobUrl, onComplete, t]);

  return (
    <Stack spacing={2}>
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            bgcolor: alpha(theme.palette.error.main, 0.12),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            "& .MuiAlert-icon": { color: theme.palette.error.light },
            "& .MuiAlert-message": { color: alpha(athenaColors.white, 0.95) },
          }}
        >
          {error}
        </Alert>
      )}

      {/* Introduction + quick guide */}
      <Box sx={athenaDarkSectionSx}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: athenaColors.white, mb: 1 }}>
          {t("contracts_wizard.consent.title", "Patient consent template")}
        </Typography>
        <Typography variant="body2" sx={{ color: alpha(athenaColors.white, 0.85), lineHeight: 1.7 }}>
          {t(
            "contracts_wizard.consent.intro",
            "To help you get started, here is a patient consent template tailored to your agreement. Download it in the language you need, review it with your team, and use it when collecting patient consent. Feel free to adapt it or use your own form instead."
          )}
        </Typography>
      </Box>

      {/* Language selection + Generate */}
      <Box sx={athenaDarkSectionSx}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: athenaColors.white, mb: 1 }}>
          {t("contracts_wizard.consent.language", "Select language")}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {LANGUAGES.map((lng) => (
            <Chip
              key={lng.code}
              label={lng.label}
              onClick={() => { setLanguage(lng.code); setBlobUrl(null); }}
              variant={language === lng.code ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                ...(language === lng.code
                  ? { bgcolor: alpha(athenaColors.periwinkle, 0.4), color: athenaColors.white }
                  : { color: alpha(athenaColors.white, 0.7), borderColor: alpha(athenaColors.periwinkle, 0.4) }),
              }}
            />
          ))}
        </Stack>

        <Button
          onClick={handleGenerate}
          disabled={generating}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={athenaDarkPrimaryButtonSx}
        >
          {t("contracts_wizard.consent.generate", blobUrl ? "Regenerate Template" : "Generate Template")}
        </Button>
      </Box>

      {/* Preview + Download */}
      {blobUrl && (
        <Box sx={athenaDarkSectionSx}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: athenaColors.white, mb: 1 }}>
            {t("contracts_wizard.consent.preview_title", "Preview")}
          </Typography>
          <Box sx={{ border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`, borderRadius: 2, overflow: "hidden", minHeight: 300 }}>
            <object data={blobUrl} type="application/pdf" width="100%" height="400px" />
          </Box>
          <Button startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ ...athenaDarkSecondaryButtonSx, mt: 1 }}>
            {t("contracts_wizard.consent.download", "Download PDF")}
          </Button>
        </Box>
      )}

      {/* Acknowledgment */}
      <Box sx={athenaDarkSectionSx}>
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(_, c) => setAcknowledged(c)}
              sx={{ color: alpha(athenaColors.white, 0.6), "&.Mui-checked": { color: athenaColors.periwinkle } }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: athenaColors.white }}>
              {t(
                "contracts_wizard.consent.acknowledgment",
                "I understand that collecting patient consent is my institution's responsibility."
              )}
            </Typography>
          }
          sx={{ alignItems: "center" }}
        />
      </Box>

      {/* Navigation */}
      <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
        <Button onClick={onBack} sx={athenaDarkSecondaryButtonSx}>
          {t("contracts_wizard.consent.back", "Back")}
        </Button>
        <Button
          onClick={handleComplete}
          disabled={!acknowledged || acknowledging}
          startIcon={acknowledging ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={athenaDarkPrimaryButtonSx}
        >
          {t("contracts_wizard.consent.complete", "Continue")}
        </Button>
      </Box>
    </Stack>
  );
}
